import { Component } from 'vue';
import { VueRenderer } from '@tiptap/vue-3';
import { createPopper, Instance as PopperInstance, Placement } from '@popperjs/core';
import { SuggestionKeyDownProps, SuggestionOptions, SuggestionProps } from '@tiptap/suggestion';

interface SuggestionsConfig {
    // the component to render when suggestions are triggered
    component: Component;
    // the props to pass to the component
    props?: Record<string, unknown>;
    placement?: Placement;
    offset?: [number, number];
    onOpen?: () => void;
    onClose?: () => void;
}

export function suggestion (config: SuggestionsConfig) {
    let renderer: VueRenderer | null = null;
    let popperInstance: PopperInstance | null = null;
    let onKeyDown: ((event: KeyboardEvent) => boolean) | null;

    const cleanup = () => {
        onKeyDown = null;

        if (popperInstance) {
            popperInstance.destroy();
            popperInstance = null;
        }

        if (renderer) {
            renderer.element?.parentNode?.removeChild(renderer.element);
            renderer.destroy();
            renderer = null;
        }

        config.onClose?.();
    };

    const setOnKeyDownHandler = (handler: (event: KeyboardEvent) => boolean) => {
        onKeyDown = handler;
    };

    const suggestionConfig: Omit<SuggestionOptions, 'editor'> = {
        allowSpaces: true,
        render: () => ({
            onStart (props: SuggestionProps) {
                cleanup();
                config.onOpen?.();

                renderer = new VueRenderer(config.component, {
                    props: {
                        ...props,
                        ...config.props,
                        setOnKeyDownHandler,
                    },
                    editor: props.editor,
                });

                if (!props.clientRect || !renderer.element) {
                    return;
                }

                const virtualReference = {
                    getBoundingClientRect: () => props.clientRect!()!,
                };

                const element = (renderer.element as unknown) as HTMLElement;
                document.body.appendChild(element);

                // Prevent the popover from causing the editor to lose focus when clicked
                element.addEventListener('mousedown', (event) => {
                    event.preventDefault();
                });
                element.addEventListener('click', (event) => {
                    // Allow link navigation (e.g. external-link in countable-hover-card)
                    if ((event.target as HTMLElement).closest('a')) return;
                    event.preventDefault();
                });

                popperInstance = createPopper(virtualReference, element, {
                    placement: config.placement ?? 'auto',
                    modifiers: [
                        {
                            name: 'offset',
                            options: {
                                offset: config.offset ?? [0, 0],
                            },
                        },
                        {
                            name: 'preventOverflow',
                            options: {
                                boundary: 'viewport',
                            },
                        },
                    ],
                });

                element.style.zIndex = '9999';
            },
            onUpdate (props: SuggestionProps) {
                renderer?.updateProps({
                    ...props,
                });
            },
            onExit () {
                cleanup();
            },
            onKeyDown (props: SuggestionKeyDownProps) {
                if (props.event.key === 'Escape') {
                    cleanup();

                    return true;
                }

                return onKeyDown ? onKeyDown(props.event) : false;
            },
        }),
    };

    return {
        suggestionConfig,
        closeSuggestions: cleanup,
        updateProps: (props: Record<string, unknown>) => {
            renderer?.updateProps(props);
        },
    };
}
